import React, { useState } from 'react';
import { withApollo } from 'react-apollo';
import { Formik } from 'formik';
import { Select, Checkbox, List } from 'antd';
import numeral from 'numeral';
import Button from '../Button';
import Form from '../Form';
import Input from '../Input';
import { ADD_DELIVERABLE } from './mutation';
import { showError, showSuccess } from '../../notifications';
const { Option } = Select;

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 8 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 16 },
  },
};

const CopyMilestoneDeliverablesForm = ({
  onComplete,
  client,
  projectId,
  children = [],
  milestoneName,
  refetchQueries = [],
  milestoneNames = [],
}) => {
  const [createNewMilestone, setCreateNewMilestone] = useState(false);
  const [copying, setCopying] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  // Filter out milestone deliverable itself, only keep child deliverables
  const deliverablesToCopy = children.filter(child => child.deliverableName !== milestoneName);

  return (
    <Formik
      initialValues={{
        targetMilestoneName: milestoneName || '',
      }}
      validate={values => {
        const errors = {};
        if (!values.targetMilestoneName) {
          errors.targetMilestoneName = 'Required';
        }
        return errors;
      }}
      onSubmit={async (values, { setSubmitting }) => {
        setCopying(true);
        setProgress({ current: 0, total: deliverablesToCopy.length });

        let successCount = 0;
        let errorCount = 0;

        // Copy each deliverable sequentially
        for (let i = 0; i < deliverablesToCopy.length; i++) {
          const deliverable = deliverablesToCopy[i];
          setProgress({ current: i + 1, total: deliverablesToCopy.length });

          try {
            await client.mutate({
              mutation: ADD_DELIVERABLE,
              variables: {
                projectId,
                deliverableName: deliverable.deliverableName,
                milestoneName: values.targetMilestoneName,
                budget: deliverable.budget || 0,
                budgetPercent: deliverable.budgetPercent || 0,
                budgetedHours: deliverable.budgetedHours || 0,
              },
            });
            successCount++;
          } catch (err) {
            console.error(`Error copying deliverable ${deliverable.deliverableName}:`, err);
            errorCount++;
          }
        }

        setCopying(false);
        setSubmitting(false);

        if (errorCount > 0) {
          showError({ 
            message: `Copied ${successCount} deliverable(s) successfully. ${errorCount} failed.` 
          });
        } else {
          showSuccess({ 
            message: `Successfully copied ${successCount} deliverable(s) to "${values.targetMilestoneName}".` 
          });
        }

        // Refetch queries to update the list
        if (refetchQueries.length > 0) {
          try {
            await Promise.all(
              refetchQueries.map(({ query, variables }) =>
                client.query({ query, variables, fetchPolicy: 'network-only' })
              )
            );
          } catch (err) {
            console.error('Error refetching queries:', err);
          }
        }

        onComplete();
      }}
    >
      {({ values, errors, touched, setFieldValue, handleChange, handleSubmit, isSubmitting }) => {
        return (
          <Form onSubmit={handleSubmit} {...formItemLayout}>
            <div className="mb-4">
              <div className="text-lg font-semibold mb-2">
                Copy {deliverablesToCopy.length} deliverable(s) from "{milestoneName}"
              </div>
              <div className="text-sm text-gray-500 mb-3">
                All deliverables will be copied with their budget and hours settings.
              </div>
            </div>

            <div className="mb-4 bg-gray-50 rounded">
              <div className="text-sm font-semibold mb-2">Deliverables to copy:</div>
              <List
                size="small"
                dataSource={deliverablesToCopy}
                renderItem={item => (
                  <List.Item>
                    <div className="flex justify-between w-full">
                      <span>{item.deliverableName}</span>
                      <span className="text-gray-500">
                        {item.budget ? numeral(item.budget).format('$0,0') : 'No budget'}
                        {item.budgetedHours ? ` • ${numeral(item.budgetedHours).format('0,0')} hrs` : ''}
                      </span>
                    </div>
                  </List.Item>
                )}
              />
            </div>

            <Form.Item label="Target Milestone" hasFeedback>
              <Select
                disabled={createNewMilestone || copying}
                name="targetMilestoneName"
                showSearch
                placeholder="Select target milestone"
                optionFilterProp="children"
                value={values.targetMilestoneName}
                onChange={category => {
                  setFieldValue('targetMilestoneName', category);
                }}
                filterOption={(input, option) =>
                  option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {milestoneNames &&
                  milestoneNames.map(name => {
                    return (
                      <Option value={name} key={name}>
                        {name}
                      </Option>
                    );
                  })}
              </Select>
              <Checkbox 
                checked={createNewMilestone} 
                onChange={e => setCreateNewMilestone(e.target.checked)}
                disabled={copying}
              >
                Create New Milestone
              </Checkbox>
            </Form.Item>

            {createNewMilestone && (
              <Form.Item
                label="New Milestone Name"
                hasFeedback
                help={errors.targetMilestoneName || 'Enter a new milestone category name'}
              >
                <Input 
                  name="targetMilestoneName" 
                  onChange={handleChange} 
                  value={values.targetMilestoneName}
                  disabled={copying}
                />
              </Form.Item>
            )}

            {copying && (
              <div className="mb-4 text-center">
                <div className="text-sm text-gray-600">
                  Copying deliverable {progress.current} of {progress.total}...
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button onClick={onComplete} disabled={copying}>
                Cancel
              </Button>
              <Button 
                loading={isSubmitting || copying} 
                type="primary" 
                htmlType="submit"
                disabled={copying}
              >
                Copy All {deliverablesToCopy.length} Deliverable(s)
              </Button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default withApollo(CopyMilestoneDeliverablesForm);

